import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './tableSimple.module.scss';
import { Pagination } from '..';
import { DEFAULT_MAX_ROWS, DEFAULT_PAGE_SIZE } from '../table/constants';

const TableSimple = ({
  children,
  header,
  footer,
  rightHeaderCells,
  pagination,
  pageSize,
  maxRows,
}) => {
  const [page, setPage] = useState(1);
  const rows = React.Children.toArray(children).slice(0, maxRows);
  const pageCount = Math.ceil(rows.length / pageSize) || 1;
  const startIndex = (page - 1) * pageSize;
  const pagedRows =
    pagination && pageCount > 1
      ? rows.slice(startIndex, startIndex + pageSize)
      : rows;

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const renderHeader = () =>
    header.map((col, i) => (
      <span
        className={cn(
          styles.headerCell,
          rightHeaderCells.includes(i) ? styles.right : styles.left
        )}
        key={i}
        role="columnheader"
      >
        {col}
      </span>
    ));

  return (
    <div className={styles.main}>
      <div className={styles.tableWrapper}>
        <div className={styles.table} role="grid">
          <div className={styles.header} role="presentation">
            <div className={styles.headerRow} role="row">
              {renderHeader()}
            </div>
          </div>
          <div className={styles.body} role="presentation">
            {pagedRows}
          </div>
        </div>
      </div>
      {footer && <div className={styles.footerRow}>{footer}</div>}
      {pagination && pageCount > 1 && (
        <div className={styles.paginationRow}>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} />
        </div>
      )}
    </div>
  );
};

export const TableRow = ({ children, onClick, noBorder }) => (
  <div
    className={noBorder ? styles.bodyRow : styles.bodyRowWithBorder}
    onClick={onClick}
    role="row"
  >
    {children}
  </div>
);

export const TableCell = ({ children, align }) => (
  <span className={cn(styles.bodyCell, styles[align])} role="gridcell">
    {children}
  </span>
);

TableSimple.defaultProps = {
  footer: null,
  rightHeaderCells: [],
  pagination: true,
  pageSize: DEFAULT_PAGE_SIZE,
  maxRows: DEFAULT_MAX_ROWS,
};

TableSimple.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.array.isRequired,
  footer: PropTypes.node,
  headerAlign: PropTypes.string,
  rightHeaderCells: PropTypes.arrayOf(PropTypes.number),
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  maxRows: PropTypes.number,
};

TableRow.defaultProps = {
  onClick: () => {},
};

TableRow.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

TableCell.defaultProps = {
  children: null,
  align: 'left',
};

TableCell.propTypes = {
  children: PropTypes.node,
  align: PropTypes.string,
};

export default TableSimple;
